import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { AuthCallbacks } from './auth/Auth';
// eslint-disable-next-line import/no-cycle
import { ApiError } from './hooks/useApiErrorMsgParser';

const CSRF_RETRIES = 3;

const CSRF_ROUTE = '/v1/csrf';

const CSRF_EXEMPT_ROUTES = [
  '/v1/auth/oidc/metadata',
  '/v1/tenants',
  '/v1/workspace',
  '/v1/emaildomains/blocklist',
  CSRF_ROUTE,
];

const INTERNAL_SERVER_ERR_MSG = 'Internal Server Error';

interface UnformattedAPIError extends Error {
  response?: { data?: { error: string } };
}

class CSRFInterceptor {
  private csrfFailures = 0;

  private apiClient: AxiosInstance;

  private gettingCsrfCookie = false;

  constructor(apiClient: AxiosInstance) {
    this.apiClient = apiClient;
  }

  createCsrfConfigInterceptor() {
    return async (axiosConfig: InternalAxiosRequestConfig<any>): Promise<InternalAxiosRequestConfig<any>> => {
      if (CSRF_EXEMPT_ROUTES.some((c) => c === axiosConfig.url)) {
        return axiosConfig;
      }

      const csrfCookie = await this.getCsrf();
      axiosConfig.headers.set('X-API-CSRF', csrfCookie);
      return axiosConfig;
    };
  }

  createServiceErrorInterceptor() {
    return (error: AxiosError<unknown>): Promise<AxiosResponse<any, any>> => {
      const errorCode = error.response?.status;
      let displayableErrorMessage = (error as UnformattedAPIError).response?.data?.error;
      if (displayableErrorMessage === INTERNAL_SERVER_ERR_MSG) {
        displayableErrorMessage = undefined;
      }

      const apiError: ApiError = { ...error, displayMessage: displayableErrorMessage };
      if (errorCode !== undefined && errorCode >= 500) {
        return Promise.reject(apiError);
      }

      if (errorCode !== undefined && errorCode === 401 && this.csrfFailures < CSRF_RETRIES) {
        // the csrf token is potentially out of sync with the access token, attempt to refresh and replay the original request
        // with original request configuration
        if (error.config?.url === CSRF_ROUTE) {
          this.csrfFailures += 1;
        }
        removeCookie('CSRF');
        return this.apiClient.request(error.config!);
      }

      return Promise.reject(apiError);
    };
  }

  async getCsrf() {
    while (this.gettingCsrfCookie) {
      // eslint-disable-next-line no-await-in-loop, no-promise-executor-return
      await new Promise((r) => setTimeout(r, 50));
    }

    const csrfCookie = getCookie('CSRF');
    if (csrfCookie !== undefined && csrfCookie !== '') {
      return csrfCookie;
    }
    this.gettingCsrfCookie = true;
    // eslint-disable-next-line no-await-in-loop
    try {
      await this.apiClient.post(CSRF_ROUTE);
      return getCookie('CSRF');
    } finally {
      this.gettingCsrfCookie = false;
    }
  }
}

export const authenticatedApiClient = (auth: AuthCallbacks): AxiosInstance => {
  const config = { baseURL: '/api' };
  const apiClient = axios.create(config);
  const csrfInterceptorInstance = new CSRFInterceptor(apiClient);
  apiClient.interceptors.request.use(async (axiosConfig) => {
    const token = await auth.getToken();
    // eslint-disable-next-line no-param-reassign
    axiosConfig.headers.Authorization = `Bearer ${token.token}`;
    axiosConfig.headers.set('X-API-VERSION', 'latest');
    return axiosConfig;
  });

  apiClient.interceptors.request.use(csrfInterceptorInstance.createCsrfConfigInterceptor());
  apiClient.interceptors.response.use(undefined, csrfInterceptorInstance.createServiceErrorInterceptor());

  return apiClient;
};

const getCookie = (cookieName: string): string | undefined =>
  document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${cookieName}=`))
    ?.split('=')[1];

const removeCookie = (cookieName: string) => {
  document.cookie = `${cookieName}=`;
};

/**
 * Gets an instance of Axios for calling the self-service API.
 */
export const getApiClient = (): AxiosInstance => {
  const config = { baseURL: '/api' };
  const apiClient = axios.create(config);
  const csrfInterceptorInstance = new CSRFInterceptor(apiClient);
  apiClient.interceptors.request.use((axiosConfig) => {
    axiosConfig.headers.set('X-API-VERSION', 'latest');
    return axiosConfig;
  });

  apiClient.interceptors.response.use(undefined, csrfInterceptorInstance.createServiceErrorInterceptor());
  return apiClient;
};
